import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import useGlobalFilterStore from '@cobliteam/hooks/src/useGlobalFilterStore';
import { useSearchData } from '@cobliteam/hooks/src/useSearchStore';
import { useLanguage, useTranslation } from '@cobliteam/i18n';
import { Button } from '@cobliteam/ui/components';
import { InputSearch } from '@cobliteam/ui/form';
import Icons from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { getTextForLanguage } from '@cobliteam/ui-business/utils';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import { CommandDialog } from './components/Command';
import GlobalSearchTriggerButton from './components/GlobalSearchTrigger';
import GlobalSearchContent from './containers/GlobalSearchContent';
import { useGlobalSidebarDef } from './globalSidebarDef';
import { trackGlobalSearchEvents } from './helpers';
import { GlobalSearchProvider } from './provider/GlobalSearchProvider';
import { ProviderPersistQueryClient } from './provider/PersisterProvider';
import { type Category, type DataWithCategory, EnumCategory, type PageWithCategory } from './types';

const INPUT_SEARCH_CLASS = 'w-full max-w-[426px] max-[960px]:hidden';

// Abrir o modal so revalida se o dataset ja passou desta idade — abre/fecha em sequencia
// serve o cache do React Query sem nenhuma request. Espelha o staleTime do app (Dashboard.tsx).
const SEARCH_DATA_STALE_TIME = 1000 * 60 * 5;

const GlobalSearchLoading = () => {
  return <InputSearch onChange={() => ''} loading size='sm' className={cn(INPUT_SEARCH_CLASS)} placeholder='Carregando a busca global...' disabled />;
};

const GlobalSearchIdleInput = ({ onOpen, isLoading }: { onOpen: () => void; isLoading: boolean }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleOpen = useCallback(() => {
    trackGlobalSearchEvents.openSearch();
    onOpen();
    setIsDialogOpen(true);
  }, [onOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleOpen();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleOpen]);

  return (
    <>
      <GlobalSearchTriggerButton onOpen={handleOpen} />
      {isLoading && (
        <CommandDialog size='lg' open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <div className='flex h-[540px] items-center justify-center'>
            <Icons name='loader-circle' className='size-6 animate-spin text-gray-9' />
          </div>
        </CommandDialog>
      )}
    </>
  );
};

const GlobalSearchError = () => {
  return (
    <div className='flex items-center justify-center'>
      <Button variant='surface' size='sm' color='danger' icon={<Icons name='x' />} />
    </div>
  );
};

const GlobalSearchTrigger = () => {
  const { t } = useTranslation('global-search');
  const { currentLanguage } = useLanguage();
  const location = useLocation().pathname;
  const vehicleGroupIds = useGlobalFilterStore((state: any) => state.vehicleGroupIds);
  const { sidebar } = useDashboard();
  const globalSidebarDef = useGlobalSidebarDef(t);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const getCategory = useCallback(
    (categoryId: string) => {
      const group = globalSidebarDef.find((category) => category.categories.find((item) => item.id === categoryId));
      return group?.categories.find((item) => item.id === categoryId);
    },
    [globalSidebarDef]
  );
  const pagesCategory = useMemo(() => getCategory(EnumCategory.PAGES), [getCategory]);

  const pagesWithCategory = useMemo<PageWithCategory[]>(() => {
    if (!pagesCategory) return [];

    return sidebar.pagesFlatted
      .filter((page) => !page.sidebar?.hide)
      .filter((page) => page.url)
      .map((page) => {
        const pageTitle = getTextForLanguage(page.title, currentLanguage);
        const pageDescription = getTextForLanguage(page.description, currentLanguage);

        return {
          ...page,
          id: pageTitle?.toLowerCase().replace(/\s+/g, '-') || '',
          primary: page.primary || '',
          category: pagesCategory,
          description: pageDescription,
        };
      });
  }, [currentLanguage, pagesCategory, sidebar.pagesFlatted]);

  const { isLoading, fuse, items, isRefetching, error, refresh, queriesUpdatedAt } = useSearchData<Category, PageWithCategory>({
    currentLocation: location,
    vehicleGroupIds,
    pagesData: pagesWithCategory,
    getCategory,
    enabled: hasBeenOpened,
  });

  // Botao "Atualizar dados da frota": revalida sempre, e o pedido explicito do usuario.
  const handleBackgroundRefresh = useCallback(() => {
    refresh({ background: true });
  }, [refresh]);

  // Abertura do modal: respeita o cache e so revalida dataset vencido.
  const handleRefreshOnOpen = useCallback(() => {
    const isStale = !queriesUpdatedAt || Date.now() - queriesUpdatedAt > SEARCH_DATA_STALE_TIME;
    if (isStale) refresh({ background: true });
  }, [queriesUpdatedAt, refresh]);

  // Erro so derruba a busca quando nao ha nada em cache pra mostrar. Com dataset
  // carregado, uma revalidacao que falha nao pode apagar a barra de busca inteira.
  if (error && !items?.length) return <GlobalSearchError />;

  if (fuse && items) {
    return (
      <GlobalSearchProvider fuse={fuse} data={items as DataWithCategory[]} initialOpen={hasBeenOpened}>
        <GlobalSearchContent handleUpdateData={handleBackgroundRefresh} handleRefreshOnOpen={handleRefreshOnOpen} isRefetching={isRefetching} />
      </GlobalSearchProvider>
    );
  }

  return <GlobalSearchIdleInput onOpen={() => setHasBeenOpened(true)} isLoading={hasBeenOpened && isLoading} />;
};

const GlobalSearch = () => {
  const { runtimeFlags } = useDashboard();
  const hasValidGroupOrVehicles = !(runtimeFlags.noValidGroupOrVehicles ?? false);
  if (!hasValidGroupOrVehicles) return null;

  return (
    <ErrorBoundary
      fallback={<GlobalSearchError />}
      onError={(error, info) => {
        DatadogTrack.error(error, { component: 'GlobalSearch', info });
      }}
    >
      <Suspense fallback={<GlobalSearchLoading />}>
        <ProviderPersistQueryClient>
          <GlobalSearchTrigger />
        </ProviderPersistQueryClient>
      </Suspense>
    </ErrorBoundary>
  );
};

export default GlobalSearch;
